import React, { forwardRef, useCallback, useState, useRef } from 'react';
import { useDropzone, DropzoneRootProps, DropzoneInputProps, FileRejection } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { FormNode } from '@/utils/api/aboboApiTypes';
import BaseQuestionType from './BaseQuestionType';

// Import shadcn components
import { Card, CardContent, Button, Alert, AlertDescription } from '@/components/ui';

// Import icons
import { Upload, FileText, CheckCircle } from 'lucide-react';

// Import utilities
import { createAttachment, createSignedUrl } from '@/utils/api';
import { s3Upload } from '@/utils/aws';
import { compressImageIfNeeded } from '@/utils/imageCompression';
import useBinaryEncryption from '@/hooks/useBinaryEncryption';
import { addFlowBreadcrumb } from '@/utils/breadcrumbs';
import { useFormWizard } from '@/contexts/FormWizardContext';
import QuestionHeading from '@/components/QuestionHeading';
import QuestionHelperText from '@/components/QuestionHelperText';

interface FileUploadProps {
  node: FormNode;
}

interface FieldType {
  value: string | null;
  error?: string;
  onChange: (value: string) => void;
}

// Loading state component
const LoadingCard: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <CardContent className="flex items-center justify-center">
      <div className="flex items-center space-x-2">
        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
        <span>{t('questionTypes.fileUpload.inprogress')}</span>
      </div>
    </CardContent>
  );
};

// Success state component
const SuccessState: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-2">
      <CheckCircle className="h-8 w-8 text-wb-grass mx-auto" />
      <p className="text-sm text-wb-grass">
        {t('questionTypes.fileUpload.uploadSuccess')}
      </p>
    </div>
  );
};

// Drag active state component
const DragActiveState: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-2">
      <Upload className="h-8 w-8 text-blue-500 mx-auto" />
      <p className="text-sm text-blue-600">
        {t('questionTypes.fileUpload.dropHere')}
      </p>
    </div>
  );
};

// Default state component
const DefaultState: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <div className="space-y-2">
      <FileText className="h-8 w-8 text-gray-400 mx-auto" />
      <div className="space-y-2">
        <Button variant="outline" className="w-auto">
          <Upload className="h-4 w-4 mr-2" />
          {t('questionTypes.fileUpload.chooseUpload')}
        </Button>
        <p className="text-xs text-gray-500">
          {t('questionTypes.fileUpload.instructions')}
        </p>
      </div>
    </div>
  );
};

// Dropzone content component
interface DropzoneContentProps {
  field: FieldType;
  isDragActive: boolean;
}

const DropzoneContent: React.FC<DropzoneContentProps> = ({ field, isDragActive }) => {
  if (field.value) return <SuccessState />;
  if (isDragActive) return <DragActiveState />;
  return <DefaultState />;
};

// Upload area component
interface UploadAreaProps {
  field: FieldType;
  isDragActive: boolean;
  getRootProps: () => DropzoneRootProps;
  getInputProps: () => DropzoneInputProps;
}

const UploadArea: React.FC<UploadAreaProps> = ({ 
  field, 
  isDragActive, 
  getRootProps, 
  getInputProps 
}) => (
  <CardContent className="p-0">
    <div 
      {...getRootProps()} 
      className={`cursor-pointer border-2 border-dashed rounded-lg p-2 text-center transition-colors ${
        isDragActive 
          ? 'border-blue-500 bg-blue-50' 
          : 'border-gray-300 hover:border-gray-400'
      }`}
    >
      <input {...getInputProps()} />
      <DropzoneContent field={field} isDragActive={isDragActive} />
    </div>
  </CardContent>
);

const FileUpload = forwardRef<HTMLDivElement, FileUploadProps>(function FileUpload(
  { node },
  ref
) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fieldRef = useRef<FieldType | null>(null);
  const encrypt = useBinaryEncryption();
  const { setAttachments } = useFormWizard();

  const validateFileUpload = (values: any[]): string | true => {
    const value = values?.at(0)?.data;

    if (node.metadata?.required && (!value || typeof value !== 'string')) {
      return t('questionTypes.fileUpload.validation.required');
    }

    return true;
  };

  const transformInput = (values: any[]) => {
    return values?.[0] || null;
  };

  const transformOutput = (value: string | null) => {
    return value ? [{ data: value }] : [];
  };

  // Rejections (wrong type / too large) are expected user behavior, not errors —
  // log them to Sentry logs for visibility into the booking flow rather than
  // captureException, which would alert on every unsupported file someone picks.
  const handleFileRejected = useCallback((reason = 'unknown', file?: File): void => {
    Sentry.logger.warn('File upload rejected', {
      reason,
      fileType: file?.type || 'unknown',
      fileSizeBytes: file?.size ?? 0,
      fileName: file?.name,
      nodeId: node.id,
    });
    setUploadError(t('errors.uploadRejected.message'));
  }, [t, node.id]);

  const onDropRejected = useCallback((rejections: FileRejection[]): void => {
    const { file, errors } = rejections[0] ?? {};
    handleFileRejected(errors?.[0]?.code ?? 'unknown', file);
  }, [handleFileRejected]);

  const onDrop = useCallback(async (files: File[]): Promise<void> => {
    const [file] = files;

    if (!file) {
      handleFileRejected('no-file');
      return;
    }

    addFlowBreadcrumb('File upload started', {
      fileType: file.type || 'unknown',
      fileSizeBytes: file.size,
      nodeId: node.id,
    });

    if (!fieldRef.current) return;

    setLoading(true);
    setUploadError(null);

    try {
      // Compress image if needed
      const processedFile = await compressImageIfNeeded(file);

      // Check if processed file is still too large
      if (processedFile.size > 5000000) {
        setLoading(false);
        Sentry.logger.warn('File exceeds size limit after compression', {
          fileType: processedFile.type,
          fileSizeBytes: processedFile.size,
          originalSizeBytes: file.size,
          nodeId: node.id,
        });
        setUploadError(t('errors.uploadRejected.message'));
        return;
      }

      const extension = processedFile.name.split('.').pop() ?? '';
      const signedUrlPromise = createSignedUrl(extension);
      const reader = new FileReader();

      reader.onload = async (): Promise<void> => {
        try {
          const { encryptedValue: encryptedFile, encryptionKey } = await encrypt(reader.result);
          const signedUrlResult = await signedUrlPromise;
          if (!signedUrlResult?.data) {
            throw new Error('Failed to get signed URL');
          }
          const { id, url } = signedUrlResult.data;

          await s3Upload(url, encryptedFile, processedFile.type);

          await createAttachment({
            id,
            encryptionKey,
            mimeType: processedFile.type,
            extension,
          });

          const dataUrl = URL.createObjectURL(processedFile);

          // Set the field value to just the attachment ID
          fieldRef.current?.onChange(id);

          // Update the attachments mapping
          setAttachments((prevAttachments) => ({ ...prevAttachments, [id]: dataUrl }));

          addFlowBreadcrumb('File upload succeeded', {
            fileType: processedFile.type,
            fileSizeBytes: processedFile.size,
            nodeId: node.id,
          });

          setLoading(false);
        } catch (error) {
          setLoading(false);
          setUploadError(t('questionTypes.fileUpload.uploadErrorTitle'));

          // Report file upload failures to Sentry
          Sentry.captureException(error, {
            tags: {
              file_upload_error: true,
              error_stage: 'upload_or_attachment',
            },
            extra: {
              fileSize: processedFile.size,
              fileType: processedFile.type,
              nodeId: node.id,
            },
          });
        }
      };

      reader.readAsArrayBuffer(processedFile);
    } catch (error) {
      setLoading(false);
      console.error('File processing error:', error);
      setUploadError(t('questionTypes.fileUpload.uploadErrorTitle'));

      // Report file processing failures to Sentry
      Sentry.captureException(error, {
        tags: {
          file_upload_error: true,
          error_stage: 'processing',
        },
        extra: {
          fileSize: file.size,
          fileType: file.type,
          nodeId: node.id,
        },
      });
    }
  }, [encrypt, t, handleFileRejected, setAttachments, node.id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxSize: 50000000, // 50MB - allow large files to reach compression
    onDrop,
    onDropRejected,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
      'application/pdf': ['.pdf'],
    },
    multiple: false,
  });

  return (
    <BaseQuestionType
      node={node}
      validate={validateFileUpload}
      transform={{
        input: transformInput,
        output: transformOutput,
      }}
    >
      {(field: FieldType) => {
        // Update the current field reference when field changes
        fieldRef.current = field;

        return (
          <div className="space-y-4" ref={ref}>
            <QuestionHeading required={node.metadata?.required}>
              {node.body}
            </QuestionHeading>

            {node.helperText && (
              <QuestionHelperText>
                {node.helperText}
              </QuestionHelperText>
            )}

            <Card className={loading ? undefined : 'p-4'}>
              {loading ? (
                <LoadingCard />
              ) : (
                <UploadArea 
                  field={field}
                  isDragActive={isDragActive}
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                />
              )}
            </Card>

            {uploadError && (
              <Alert variant="destructive">
                <AlertDescription>{uploadError}</AlertDescription>
              </Alert>
            )}

            {field.error && (
              <p className="text-red-500 text-sm text-center">{field.error}</p>
            )}
          </div>
        );
      }}
    </BaseQuestionType>
  );
});

export default FileUpload;